import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "stores/index";

/**
 * Check layout type
 */
export type CheckLayoutType = "check-top" | "check-middle" | "check-bottom";

/**
 * Template category - AP (vendor payments) or Payroll (employee wages)
 */
export type TemplateCategory = "ap" | "payroll";

/**
 * Check element type - items that can be positioned on the check
 * Note: Only includes FILLABLE elements - pre-printed items (company, bank, MICR) are on the check stock
 */
export type CheckElementType =
  // Check section - fillable fields only (common to both AP and Payroll)
  | "date"
  | "amount_numbers"
  | "payee_name"
  | "payee_address"
  | "amount_words"
  | "memo"
  // AP Stub section - vendor payment details
  | "stub_vendor_name"
  | "stub_date"
  | "stub_total"
  // AP Stub invoice table columns
  | "stub_col_invoice_num"
  | "stub_col_invoice_date"
  | "stub_col_description"
  | "stub_col_amount"
  | "stub_col_discount"
  | "stub_col_net"
  // Payroll Stub section - employer info
  | "stub_employer_name"
  | "stub_employer_address"
  | "stub_employer_phone"
  // Payroll Stub section - employee info
  | "stub_employee_name"
  | "stub_employee_address"
  // Payroll Stub section - labels
  | "stub_employer_label"
  | "stub_employee_label"
  | "stub_memo_label"
  // Payroll Stub section - pay period
  | "stub_pay_period_header"
  | "stub_pay_period_begin"
  | "stub_pay_period_end"
  | "stub_pay_date"
  // Payroll Stub section - benefits table
  | "stub_benefits_header"
  | "stub_col_benefit_type"
  | "stub_col_benefit_used"
  | "stub_col_benefit_available"
  // Payroll Stub section - earnings table
  | "stub_earnings_header"
  | "stub_col_pay_type"
  | "stub_col_hours"
  | "stub_col_rate"
  | "stub_col_current"
  | "stub_col_ytd"
  // Payroll Stub section - taxes table
  | "stub_taxes_header"
  | "stub_col_tax_type"
  | "stub_col_tax_current"
  | "stub_col_tax_ytd"
  // Payroll Stub section - deductions table
  | "stub_deductions_header"
  | "stub_col_deduction_type"
  | "stub_col_deduction_current"
  | "stub_col_deduction_ytd"
  // Payroll Stub section - contributions table
  | "stub_contributions_header"
  | "stub_col_contribution_type"
  | "stub_col_contribution_current"
  | "stub_col_contribution_ytd"
  // Payroll Stub section - summary
  | "stub_summary_header"
  | "stub_col_summary_type"
  | "stub_col_summary_current"
  | "stub_col_summary_ytd"
  | "stub_net_pay"
  | "stub_net_pay_label";

/**
 * Element type definitions with labels and default properties
 */
export interface ElementTypeDefinition {
  type: CheckElementType;
  label: string;
  category: "check" | "stub";
  templateCategory?: "ap" | "payroll"; // Which template type this element belongs to (stub only)
  group?: string; // UI grouping for element picker menu
  dataField?: string; // Data field this element binds to (for documentation/data binding)
  defaultWidth: number;
  defaultFontSize: number;
  defaultFontWeight: "normal" | "bold";
  defaultAlignment: "left" | "center" | "right";
}

/**
 * Available element types for the check section
 * These appear on the actual check portion (not stub)
 */
export const CHECK_ELEMENT_TYPES: ElementTypeDefinition[] = [
  { type: "date", label: "Check Date", category: "check", group: "Check Fields", dataField: "check.date", defaultWidth: 1.5, defaultFontSize: 10, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "amount_numbers", label: "Amount (Numbers)", category: "check", group: "Check Fields", dataField: "check.amount", defaultWidth: 1.0, defaultFontSize: 12, defaultFontWeight: "bold", defaultAlignment: "right" },
  { type: "payee_name", label: "Payee Name", category: "check", group: "Check Fields", dataField: "payee.name", defaultWidth: 4.5, defaultFontSize: 11, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "payee_address", label: "Payee Address", category: "check", group: "Check Fields", dataField: "payee.address", defaultWidth: 3.5, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "amount_words", label: "Amount (Words)", category: "check", group: "Check Fields", dataField: "check.amountWords", defaultWidth: 6.5, defaultFontSize: 10, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "memo", label: "Memo Line", category: "check", group: "Check Fields", dataField: "check.memo", defaultWidth: 3.0, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "left" },
];

/**
 * Available element types for AP stub sections (vendor payments)
 */
export const AP_STUB_ELEMENT_TYPES: ElementTypeDefinition[] = [
  // Vendor Info
  { type: "stub_vendor_name", label: "Vendor Name", category: "stub", templateCategory: "ap", group: "Vendor Info", dataField: "vendor.name", defaultWidth: 4.0, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_date", label: "Check Date", category: "stub", templateCategory: "ap", group: "Vendor Info", dataField: "check.date", defaultWidth: 1.5, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_total", label: "Total Amount", category: "stub", templateCategory: "ap", group: "Vendor Info", dataField: "check.total", defaultWidth: 1.5, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "right" },
  // Invoice Table Columns
  { type: "stub_col_invoice_num", label: "Invoice # Column", category: "stub", templateCategory: "ap", group: "Invoice Table", dataField: "invoices[].number", defaultWidth: 1.0, defaultFontSize: 8, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_invoice_date", label: "Invoice Date Column", category: "stub", templateCategory: "ap", group: "Invoice Table", dataField: "invoices[].date", defaultWidth: 0.8, defaultFontSize: 8, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_description", label: "Description Column", category: "stub", templateCategory: "ap", group: "Invoice Table", dataField: "invoices[].description", defaultWidth: 2.5, defaultFontSize: 8, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_amount", label: "Amount Column", category: "stub", templateCategory: "ap", group: "Invoice Table", dataField: "invoices[].amount", defaultWidth: 1.0, defaultFontSize: 8, defaultFontWeight: "bold", defaultAlignment: "right" },
  { type: "stub_col_discount", label: "Discount Column", category: "stub", templateCategory: "ap", group: "Invoice Table", dataField: "invoices[].discount", defaultWidth: 0.9, defaultFontSize: 8, defaultFontWeight: "bold", defaultAlignment: "right" },
  { type: "stub_col_net", label: "Net Column", category: "stub", templateCategory: "ap", group: "Invoice Table", dataField: "invoices[].net", defaultWidth: 0.9, defaultFontSize: 8, defaultFontWeight: "bold", defaultAlignment: "right" },
];

/**
 * Available element types for Payroll stub sections (employee wages)
 * Organized by group with dataField for data binding documentation
 */
export const PAYROLL_STUB_ELEMENT_TYPES: ElementTypeDefinition[] = [
  // ========== EMPLOYER INFO ==========
  { type: "stub_employer_label", label: "\"EMPLOYER\" Label", category: "stub", templateCategory: "payroll", group: "Employer Info", dataField: "static:EMPLOYER", defaultWidth: 1.5, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_employer_name", label: "Company Name", category: "stub", templateCategory: "payroll", group: "Employer Info", dataField: "employer.name", defaultWidth: 3.0, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_employer_address", label: "Company Address", category: "stub", templateCategory: "payroll", group: "Employer Info", dataField: "employer.address", defaultWidth: 3.0, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_employer_phone", label: "Company Phone", category: "stub", templateCategory: "payroll", group: "Employer Info", dataField: "employer.phone", defaultWidth: 1.5, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "left" },

  // ========== EMPLOYEE INFO ==========
  { type: "stub_employee_label", label: "\"EMPLOYEE\" Label", category: "stub", templateCategory: "payroll", group: "Employee Info", dataField: "static:EMPLOYEE", defaultWidth: 1.5, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_employee_name", label: "Employee Name", category: "stub", templateCategory: "payroll", group: "Employee Info", dataField: "employee.name", defaultWidth: 3.0, defaultFontSize: 10, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_employee_address", label: "Employee Address", category: "stub", templateCategory: "payroll", group: "Employee Info", dataField: "employee.address", defaultWidth: 3.0, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "left" },

  // ========== PAY PERIOD ==========
  { type: "stub_pay_period_header", label: "\"PAY PERIOD\" Header", category: "stub", templateCategory: "payroll", group: "Pay Period", dataField: "static:PAY PERIOD", defaultWidth: 2.0, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_pay_period_begin", label: "Period Beginning", category: "stub", templateCategory: "payroll", group: "Pay Period", dataField: "payPeriod.begin", defaultWidth: 1.8, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_pay_period_end", label: "Period Ending", category: "stub", templateCategory: "payroll", group: "Pay Period", dataField: "payPeriod.end", defaultWidth: 1.8, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_pay_date", label: "Pay Date", category: "stub", templateCategory: "payroll", group: "Pay Period", dataField: "payPeriod.payDate", defaultWidth: 1.8, defaultFontSize: 9, defaultFontWeight: "normal", defaultAlignment: "right" },

  // ========== BENEFITS TABLE ==========
  { type: "stub_benefits_header", label: "\"BENEFITS\" Header", category: "stub", templateCategory: "payroll", group: "Benefits Table", dataField: "static:BENEFITS", defaultWidth: 3.0, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_benefit_type", label: "Benefit Type Column", category: "stub", templateCategory: "payroll", group: "Benefits Table", dataField: "benefits[].type", defaultWidth: 1.2, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_col_benefit_used", label: "Used Column", category: "stub", templateCategory: "payroll", group: "Benefits Table", dataField: "benefits[].used", defaultWidth: 0.8, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_benefit_available", label: "Available Column", category: "stub", templateCategory: "payroll", group: "Benefits Table", dataField: "benefits[].available", defaultWidth: 0.8, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },

  // ========== EARNINGS/PAY TABLE ==========
  { type: "stub_earnings_header", label: "\"PAY\" Header", category: "stub", templateCategory: "payroll", group: "Earnings Table", dataField: "static:PAY", defaultWidth: 2.5, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_pay_type", label: "Pay Type Column", category: "stub", templateCategory: "payroll", group: "Earnings Table", dataField: "earnings[].type", defaultWidth: 1.5, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_col_hours", label: "Hours Column", category: "stub", templateCategory: "payroll", group: "Earnings Table", dataField: "earnings[].hours", defaultWidth: 0.7, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_rate", label: "Rate Column", category: "stub", templateCategory: "payroll", group: "Earnings Table", dataField: "earnings[].rate", defaultWidth: 0.7, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_current", label: "Current Amount Column", category: "stub", templateCategory: "payroll", group: "Earnings Table", dataField: "earnings[].current", defaultWidth: 0.9, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_ytd", label: "YTD Amount Column", category: "stub", templateCategory: "payroll", group: "Earnings Table", dataField: "earnings[].ytd", defaultWidth: 1.0, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },

  // ========== TAXES TABLE ==========
  { type: "stub_taxes_header", label: "\"TAXES\" Header", category: "stub", templateCategory: "payroll", group: "Taxes Table", dataField: "static:TAXES", defaultWidth: 2.5, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_tax_type", label: "Tax Type Column", category: "stub", templateCategory: "payroll", group: "Taxes Table", dataField: "taxes[].type", defaultWidth: 2.5, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_col_tax_current", label: "Tax Current Column", category: "stub", templateCategory: "payroll", group: "Taxes Table", dataField: "taxes[].current", defaultWidth: 0.9, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_tax_ytd", label: "Tax YTD Column", category: "stub", templateCategory: "payroll", group: "Taxes Table", dataField: "taxes[].ytd", defaultWidth: 1.0, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },

  // ========== DEDUCTIONS TABLE ==========
  { type: "stub_deductions_header", label: "\"DEDUCTIONS\" Header", category: "stub", templateCategory: "payroll", group: "Deductions Table", dataField: "static:DEDUCTIONS", defaultWidth: 2.5, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_deduction_type", label: "Deduction Type Column", category: "stub", templateCategory: "payroll", group: "Deductions Table", dataField: "deductions[].type", defaultWidth: 2.5, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_col_deduction_current", label: "Deduction Current Column", category: "stub", templateCategory: "payroll", group: "Deductions Table", dataField: "deductions[].current", defaultWidth: 0.9, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_deduction_ytd", label: "Deduction YTD Column", category: "stub", templateCategory: "payroll", group: "Deductions Table", dataField: "deductions[].ytd", defaultWidth: 1.0, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },

  // ========== CONTRIBUTIONS/OTHER PAY TABLE ==========
  { type: "stub_contributions_header", label: "\"OTHER PAY/CONTRIBUTIONS\" Header", category: "stub", templateCategory: "payroll", group: "Contributions Table", dataField: "static:OTHER PAY/CONTRIBUTIONS", defaultWidth: 3.0, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_contribution_type", label: "Contribution Type Column", category: "stub", templateCategory: "payroll", group: "Contributions Table", dataField: "contributions[].type", defaultWidth: 2.5, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_col_contribution_current", label: "Contribution Current Column", category: "stub", templateCategory: "payroll", group: "Contributions Table", dataField: "contributions[].current", defaultWidth: 0.9, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_contribution_ytd", label: "Contribution YTD Column", category: "stub", templateCategory: "payroll", group: "Contributions Table", dataField: "contributions[].ytd", defaultWidth: 1.0, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },

  // ========== SUMMARY TABLE ==========
  { type: "stub_summary_header", label: "\"SUMMARY\" Header", category: "stub", templateCategory: "payroll", group: "Summary", dataField: "static:SUMMARY", defaultWidth: 2.0, defaultFontSize: 10, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_col_summary_type", label: "Summary Type Column", category: "stub", templateCategory: "payroll", group: "Summary", dataField: "summary[].type", defaultWidth: 1.2, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "left" },
  { type: "stub_col_summary_current", label: "Summary Current Column", category: "stub", templateCategory: "payroll", group: "Summary", dataField: "summary[].current", defaultWidth: 1.0, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },
  { type: "stub_col_summary_ytd", label: "Summary YTD Column", category: "stub", templateCategory: "payroll", group: "Summary", dataField: "summary[].ytd", defaultWidth: 1.2, defaultFontSize: 8, defaultFontWeight: "normal", defaultAlignment: "right" },

  // ========== NET PAY ==========
  { type: "stub_net_pay_label", label: "\"Net Pay:\" Label", category: "stub", templateCategory: "payroll", group: "Net Pay", dataField: "static:Net Pay", defaultWidth: 1.0, defaultFontSize: 11, defaultFontWeight: "bold", defaultAlignment: "left" },
  { type: "stub_net_pay", label: "Net Pay Amount", category: "stub", templateCategory: "payroll", group: "Net Pay", dataField: "netPay", defaultWidth: 1.5, defaultFontSize: 12, defaultFontWeight: "bold", defaultAlignment: "right" },

  // ========== MEMO ==========
  { type: "stub_memo_label", label: "\"MEMO:\" Label", category: "stub", templateCategory: "payroll", group: "Other", dataField: "static:MEMO:", defaultWidth: 1.0, defaultFontSize: 9, defaultFontWeight: "bold", defaultAlignment: "left" },
];

/**
 * Combined stub element types (for backwards compatibility)
 */
export const STUB_ELEMENT_TYPES: ElementTypeDefinition[] = [
  ...AP_STUB_ELEMENT_TYPES,
  ...PAYROLL_STUB_ELEMENT_TYPES,
];

/**
 * Check section type - position-based (top, middle, bottom of the page)
 */
export type CheckSectionType = "top" | "middle" | "bottom";

/**
 * Section content type - what type of content is in a section
 */
export type SectionContentType = "check" | "stub";

/**
 * Get the content type for a section based on layout
 */
export const getSectionContentType = (
  section: CheckSectionType,
  layoutType: CheckLayoutType
): SectionContentType => {
  switch (layoutType) {
    case "check-top":
      return section === "top" ? "check" : "stub";
    case "check-middle":
      return section === "middle" ? "check" : "stub";
    case "check-bottom":
      return section === "bottom" ? "check" : "stub";
    default:
      return "stub";
  }
};

/**
 * Get all available element types for a section based on its content type and template category
 */
export const getAvailableElementTypes = (
  section: CheckSectionType,
  layoutType: CheckLayoutType,
  templateCategory: TemplateCategory = "ap"
): ElementTypeDefinition[] => {
  const contentType = getSectionContentType(section, layoutType);
  if (contentType === "check") {
    return CHECK_ELEMENT_TYPES;
  }
  // Return stub elements filtered by template category
  if (templateCategory === "payroll") {
    return PAYROLL_STUB_ELEMENT_TYPES;
  }
  return AP_STUB_ELEMENT_TYPES;
};

/**
 * Check template element with configurable position
 */
export interface CheckTemplateElement {
  id: string;
  elementType: CheckElementType;
  section: CheckSectionType;
  label: string;
  x: number; // inches from left edge of section
  y: number; // inches from top edge of section
  width: number; // max width in inches
  fontSize: number;
  fontWeight: "normal" | "bold";
  alignment: "left" | "center" | "right";
  visible: boolean;
  sortOrder?: number;
  // Data binding fields
  dataBinding?: string; // Path to payroll data (e.g., "workerFullName", "taxesWithheld[].name")
  arraySource?: string; // For array bindings, the parent array (e.g., "taxesWithheld")
  rowGroupId?: string; // Elements with same ID repeat together for arrays
  rowSpacing?: number; // Y offset per row (inches)
  maxRows?: number; // Maximum rows to display
}

/**
 * Data binding option for the dropdown
 */
export interface DataBindingOption {
  path: string; // The binding path (e.g., "workerFullName")
  label: string; // Human-readable label
  category: string; // Grouping category
  arraySource?: string; // If this is an array binding, the source array
  format?: "string" | "currency" | "date" | "number"; // How to format the value
  placeholder?: boolean; // If true, data is not available from Everee
}

/**
 * Everee payroll data binding options
 * These are the fields available from the Everee API
 */
export const EVEREE_DATA_BINDINGS: DataBindingOption[] = [
  // Employee Info
  { path: "workerFullName", label: "Employee Name", category: "Employee", format: "string" },
  { path: "paymentId", label: "Payment/Check Number", category: "Employee", format: "number" },

  // Dates
  { path: "payDate", label: "Pay Date", category: "Dates", format: "date" },
  { path: "payStubDate", label: "Stub Date", category: "Dates", format: "date" },

  // Amounts (totals)
  { path: "grossEarnings.amount", label: "Gross Earnings", category: "Amounts", format: "currency" },
  { path: "netEarnings.amount", label: "Net Pay", category: "Amounts", format: "currency" },
  { path: "totalTaxesWithheld.amount", label: "Total Taxes Withheld", category: "Amounts", format: "currency" },
  { path: "preTaxDeductions.amount", label: "Pre-Tax Deductions Total", category: "Amounts", format: "currency" },
  { path: "postTaxDeductions.amount", label: "Post-Tax Deductions Total", category: "Amounts", format: "currency" },
  { path: "deferredCompensation.amount", label: "Deferred Compensation", category: "Amounts", format: "currency" },

  // Taxes Withheld Array
  { path: "taxesWithheld[].name", label: "Tax Name", category: "Taxes (Array)", arraySource: "taxesWithheld", format: "string" },
  { path: "taxesWithheld[].amount.amount", label: "Tax Amount (Current)", category: "Taxes (Array)", arraySource: "taxesWithheld", format: "currency" },
  { path: "taxesWithheld[].amountYearToDate.amount", label: "Tax Amount (YTD)", category: "Taxes (Array)", arraySource: "taxesWithheld", format: "currency" },

  // Deductions Array
  { path: "deductions[].name", label: "Deduction Name", category: "Deductions (Array)", arraySource: "deductions", format: "string" },
  { path: "deductions[].amount.amount", label: "Deduction Amount (Current)", category: "Deductions (Array)", arraySource: "deductions", format: "currency" },
  { path: "deductions[].amountYearToDate.amount", label: "Deduction Amount (YTD)", category: "Deductions (Array)", arraySource: "deductions", format: "currency" },

  // Deposits Array (Direct Deposit)
  { path: "deposits[].destinationLabel", label: "Deposit Destination", category: "Deposits (Array)", arraySource: "deposits", format: "string" },
  { path: "deposits[].amount.amount", label: "Deposit Amount", category: "Deposits (Array)", arraySource: "deposits", format: "currency" },

  // Placeholder fields - NOT available from Everee API
  { path: "earnings[].name", label: "Earning Type", category: "Earnings (Placeholder)", arraySource: "earnings", format: "string", placeholder: true },
  { path: "earnings[].hours", label: "Hours Worked", category: "Earnings (Placeholder)", arraySource: "earnings", format: "number", placeholder: true },
  { path: "earnings[].rate", label: "Hourly Rate", category: "Earnings (Placeholder)", arraySource: "earnings", format: "currency", placeholder: true },
  { path: "earnings[].amount", label: "Earning Amount (Current)", category: "Earnings (Placeholder)", arraySource: "earnings", format: "currency", placeholder: true },
  { path: "earnings[].ytd", label: "Earning Amount (YTD)", category: "Earnings (Placeholder)", arraySource: "earnings", format: "currency", placeholder: true },

  // Other placeholder fields
  { path: "payPeriodStart", label: "Pay Period Start", category: "Pay Period (Placeholder)", format: "date", placeholder: true },
  { path: "payPeriodEnd", label: "Pay Period End", category: "Pay Period (Placeholder)", format: "date", placeholder: true },
  { path: "workerAddress.line1", label: "Employee Address Line 1", category: "Address (Placeholder)", format: "string", placeholder: true },
  { path: "workerAddress.city", label: "Employee City", category: "Address (Placeholder)", format: "string", placeholder: true },
  { path: "workerAddress.state", label: "Employee State", category: "Address (Placeholder)", format: "string", placeholder: true },
  { path: "workerAddress.zip", label: "Employee ZIP", category: "Address (Placeholder)", format: "string", placeholder: true },
];

/**
 * Get data binding options grouped by category
 */
export const getDataBindingsByCategory = (): Record<string, DataBindingOption[]> => {
  return EVEREE_DATA_BINDINGS.reduce((acc, binding) => {
    if (!acc[binding.category]) {
      acc[binding.category] = [];
    }
    acc[binding.category].push(binding);
    return acc;
  }, {} as Record<string, DataBindingOption[]>);
};

/**
 * Find a data binding option by path
 */
export const findDataBinding = (path: string): DataBindingOption | undefined => {
  return EVEREE_DATA_BINDINGS.find((b) => b.path === path);
};

/**
 * Check template with configurable positioning
 */
export interface CheckTemplate {
  id: string;
  name: string;
  companyCode: string;
  layoutType: CheckLayoutType;
  templateCategory: TemplateCategory; // "ap" for vendor checks, "payroll" for employee checks

  // Section heights (in inches)
  stubTopHeight: number;
  checkHeight: number;
  stubBottomHeight: number;
  pageHeight: number; // total page height

  // Global position offsets (for fine-tuning alignment with check stock)
  offsetX: number;
  offsetY: number;

  // Page margins
  marginTop: number;
  marginLeft: number;
  marginRight: number;

  // Check elements with positions
  elements: CheckTemplateElement[];

  // Company/bank info - stored for API compatibility but pre-printed on check stock
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  bankName: string;
  bankCity: string;
  bankRouting: string;

  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Sample check data for preview (common fields)
 */
export interface SampleCheckData {
  payeeName: string;
  payeeAddress: string;
  payeeCity: string;
  payeeState: string;
  payeeZip: string;
  amount: number;
  date: string;
  checkNumber: string;
  memo: string;
}

/**
 * Payroll Check Data Structure
 * This interface documents the data structure expected when printing payroll checks.
 * Use this as a reference when binding template elements to actual payroll data.
 *
 * Data Field Mapping:
 * - static:* = Static label text (no data binding needed)
 * - employer.* = Employer/company information
 * - employee.* = Employee information (same as payee for payroll)
 * - payPeriod.* = Pay period dates
 * - benefits[] = Array of benefit balances (vacation, sick, etc.)
 * - earnings[] = Array of pay types (salary, overtime, bonus, etc.)
 * - taxes[] = Array of tax withholdings
 * - deductions[] = Array of deductions (medical, 401k, etc.)
 * - contributions[] = Array of employer contributions
 * - summary.* = Summary totals
 * - netPay = Final net pay amount
 */
export interface PayrollCheckData {
  // Check portion data
  check: {
    date: string;
    amount: number;
    amountWords: string;
    checkNumber: string;
    memo: string;
  };

  // Payee (Employee)
  payee: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  };

  // Employer
  employer: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };

  // Pay Period
  payPeriod: {
    begin: string;
    end: string;
    payDate: string;
  };

  // Benefits balances (vacation, sick time, etc.)
  benefits: Array<{
    type: string;      // e.g., "Vacation", "Sick"
    used: number;      // Hours/days used this period
    available: number; // Remaining balance
  }>;

  // Earnings (pay types)
  earnings: Array<{
    type: string;        // e.g., "Salary", "Overtime", "Bonus"
    hours: number | null;
    rate: number | null;
    current: number;     // Amount this period
    ytd: number;         // Year-to-date total
  }>;

  // Tax withholdings
  taxes: Array<{
    type: string;    // e.g., "Federal Income Tax", "Social Security"
    current: number;
    ytd: number;
  }>;

  // Deductions (employee contributions)
  deductions: Array<{
    type: string;    // e.g., "Medical (Pre-Tax)", "401k"
    current: number;
    ytd: number;
  }>;

  // Employer contributions (shown on stub but not deducted)
  contributions: Array<{
    type: string;
    current: number;
    ytd: number;
  }>;

  // Summary totals
  summary: {
    totalPay: { current: number; ytd: number };
    totalTaxes: { current: number; ytd: number };
    totalDeductions: { current: number; ytd: number };
  };

  // Final net pay
  netPay: number;
}

/**
 * Sample payroll data for preview
 */
export interface SamplePayrollData extends SampleCheckData {
  // Employer info
  employerName: string;
  employerAddress: string;
  employerCity: string;
  employerState: string;
  employerZip: string;
  employerPhone: string;
  // Employee info (payeeName, payeeAddress are inherited)
  // Pay period
  periodBegin: string;
  periodEnd: string;
  payDate: string;
  // Benefits
  benefits: Array<{ type: string; used: number; available: number }>;
  // Earnings
  earnings: Array<{
    type: string;
    hours: number | null;
    rate: number | null;
    current: number;
    ytd: number;
  }>;
  // Taxes
  taxes: Array<{ type: string; current: number; ytd: number }>;
  // Deductions
  deductions: Array<{ type: string; current: number; ytd: number }>;
  // Contributions
  contributions: Array<{ type: string; current: number; ytd: number }>;
  // Summary
  summary: {
    totalPay: { current: number; ytd: number };
    totalTaxes: { current: number; ytd: number };
    totalDeductions: { current: number; ytd: number };
  };
  netPay: number;
}

/**
 * Check Template Designer Store
 */
class CheckTemplateDesignerStore {
  rootStore: RootStore;

  // Templates
  templates: CheckTemplate[] = [];
  selectedTemplateId: string | null = null;

  // Editing state
  editingTemplate: CheckTemplate | null = null;
  isDirty = false;

  // Selected element for editing
  selectedElementId: string | null = null;

  // Preview scale
  previewScale = 0.8;

  // UI State
  loading = false;
  error: string | null = null;

  // Sample data for AP preview
  sampleData: SampleCheckData = {
    payeeName: "ABC Supply Company",
    payeeAddress: "1234 Industrial Blvd",
    payeeCity: "Seattle",
    payeeState: "WA",
    payeeZip: "98101",
    amount: 12345.67,
    date: new Date().toISOString().split("T")[0],
    checkNumber: "051957",
    memo: "Invoice #12345",
  };

  // Sample Everee API format data for data binding preview
  // This matches the actual Everee API response format
  sampleEvereeData = {
    workerId: "64a7d415-ba2f-4afc-85e6-3320e00ba1b2",
    workerFullName: "Shannon D. Manring",
    paymentId: 487030,
    payStubDate: "2025-12-23",
    payDate: "2025-12-26",
    grossEarnings: { amount: "3076.92", currency: "USD" },
    totalTaxesWithheld: { amount: "568.71", currency: "USD" },
    preTaxDeductions: { amount: "304.17", currency: "USD" },
    postTaxDeductions: { amount: "10.12", currency: "USD" },
    deferredCompensation: { amount: "0.00", currency: "USD" },
    netEarnings: { amount: "2193.92", currency: "USD" },
    payableNotes: ["Hours Worked @ Sound Precast - Dec 2025"],
    taxesWithheld: [
      { name: "Federal Income Tax", amount: { amount: "287.25", currency: "USD" }, amountYearToDate: { amount: "6224.92", currency: "USD" } },
      { name: "Social Security", amount: { amount: "185.20", currency: "USD" }, amountYearToDate: { amount: "4693.63", currency: "USD" } },
      { name: "Medicare", amount: { amount: "43.31", currency: "USD" }, amountYearToDate: { amount: "1097.70", currency: "USD" } },
      { name: "WA Workers' Comp", amount: { amount: "14.85", currency: "USD" }, amountYearToDate: { amount: "212.66", currency: "USD" } },
      { name: "WA PFML", amount: { amount: "20.25", currency: "USD" }, amountYearToDate: { amount: "513.52", currency: "USD" } },
      { name: "WA Cares Fund", amount: { amount: "17.85", currency: "USD" }, amountYearToDate: { amount: "452.67", currency: "USD" } },
    ],
    deductions: [
      { name: "Medical (Pre-Tax)", amount: { amount: "89.79", currency: "USD" }, amountYearToDate: { amount: "2334.54", currency: "USD" } },
      { name: "Life (Post-Tax)", amount: { amount: "9.12", currency: "USD" }, amountYearToDate: { amount: "237.12", currency: "USD" } },
      { name: "401k (Pre-Tax)", amount: { amount: "215.38", currency: "USD" }, amountYearToDate: { amount: "3513.40", currency: "USD" } },
    ],
    deposits: [
      { destinationLabel: "US Bank: XXXXXXXX1234", amount: { amount: "2193.92", currency: "USD" } },
    ],
    // Placeholder data for earnings (not from Everee API)
    earnings: [
      { name: "Salary", hours: null, rate: null, amount: "3076.92", ytd: "66411.90" },
      { name: "Sick Pay", hours: null, rate: null, amount: "0.00", ytd: "939.03" },
      { name: "Vacation Pay", hours: null, rate: null, amount: "0.00", ytd: "6648.88" },
    ],
    // Placeholder for pay period
    payPeriodStart: "2025-12-07",
    payPeriodEnd: "2025-12-20",
    // Placeholder for address
    workerAddress: {
      line1: "148 S Military Rd",
      city: "Winlock",
      state: "WA",
      zip: "98596-9322",
    },
  };

  // Sample data for Payroll preview (based on image)
  samplePayrollData: SamplePayrollData = {
    // Check fields
    payeeName: "Shannon D. Manring",
    payeeAddress: "148 S Military Rd",
    payeeCity: "Winlock",
    payeeState: "WA",
    payeeZip: "98596-9322",
    amount: 2193.92,
    date: "2025-12-26",
    checkNumber: "001234",
    memo: "",
    // Employer info
    employerName: "SOUND PRECAST LLC",
    employerAddress: "PO Box 245",
    employerCity: "Castle Rock",
    employerState: "WA",
    employerZip: "98611-0245",
    employerPhone: "509-661-1789",
    // Pay period
    periodBegin: "2025-12-07",
    periodEnd: "2025-12-20",
    payDate: "2025-12-26",
    // Benefits
    benefits: [
      { type: "Vacation", used: 0.0, available: -16.0 },
      { type: "Sick", used: 0.0, available: 45.74 },
    ],
    // Earnings
    earnings: [
      { type: "Salary", hours: null, rate: null, current: 3076.92, ytd: 66411.9 },
      { type: "Sick Pay", hours: null, rate: null, current: 0.0, ytd: 939.03 },
      { type: "Vacation Pay", hours: null, rate: null, current: 0.0, ytd: 6648.88 },
      { type: "Holiday Pay", hours: null, rate: null, current: 0.0, ytd: 1538.45 },
      { type: "Bonus", hours: null, rate: null, current: 0.0, ytd: 2500.0 },
    ],
    // Taxes
    taxes: [
      { type: "Federal Income Tax", current: 287.25, ytd: 6224.92 },
      { type: "Social Security", current: 185.2, ytd: 4693.63 },
      { type: "Medicare", current: 43.31, ytd: 1097.7 },
      { type: "WA Workers' Comp Tax", current: 14.85, ytd: 212.66 },
      { type: "WA PFML", current: 20.25, ytd: 513.52 },
      { type: "WA Cares Fund", current: 17.85, ytd: 452.67 },
    ],
    // Deductions
    deductions: [
      { type: "Employee Medical (Pre-Tax)", current: 89.79, ytd: 2334.54 },
      { type: "Life (Post-Tax)", current: 9.12, ytd: 237.12 },
      { type: "401k (Pre-Tax)", current: 215.38, ytd: 3513.4 },
    ],
    // Contributions
    contributions: [
      { type: "Employee Medical (Pre-Tax)", current: 269.36, ytd: 7003.36 },
      { type: "401k (Pre-Tax)", current: 123.08, ytd: 2007.73 },
    ],
    // Summary
    summary: {
      totalPay: { current: 3076.92, ytd: 78038.26 },
      totalTaxes: { current: 568.71, ytd: 13195.1 },
      totalDeductions: { current: 314.29, ytd: 6085.06 },
    },
    netPay: 2193.92,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Computed ====================

  get selectedTemplate(): CheckTemplate | null {
    if (!this.selectedTemplateId) return null;
    return this.templates.find((t) => t.id === this.selectedTemplateId) || null;
  }

  get selectedElement(): CheckTemplateElement | null {
    if (!this.editingTemplate || !this.selectedElementId) return null;
    return (
      this.editingTemplate.elements.find((e) => e.id === this.selectedElementId) ||
      null
    );
  }

  get topElements(): CheckTemplateElement[] {
    if (!this.editingTemplate) return [];
    return this.editingTemplate.elements.filter((e) => e.section === "top");
  }

  get middleElements(): CheckTemplateElement[] {
    if (!this.editingTemplate) return [];
    return this.editingTemplate.elements.filter((e) => e.section === "middle");
  }

  get bottomElements(): CheckTemplateElement[] {
    if (!this.editingTemplate) return [];
    return this.editingTemplate.elements.filter((e) => e.section === "bottom");
  }

  /**
   * Get the content type (check or stub) for a given section based on current layout
   */
  getSectionContentType(section: CheckSectionType): SectionContentType {
    if (!this.editingTemplate) return "stub";
    return getSectionContentType(section, this.editingTemplate.layoutType);
  }

  // ==================== Actions ====================

  async loadData(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const companyCode = this.rootStore.appLayoutStore?.selectedCompanyId;
      const checkTemplatesStore = this.rootStore.serviceStores?.["check-templates"];
      const checkTemplateElementsStore = this.rootStore.serviceStores?.["check-template-elements"];

      if (checkTemplatesStore && checkTemplateElementsStore) {
        // 1. Load templates
        const templatesResult = await checkTemplatesStore.find({
          query: companyCode ? { companyCode } : {},
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiTemplates: any[] = templatesResult.data || templatesResult;

        // 2. Load elements for all templates
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let apiElements: any[] = [];
        if (apiTemplates.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const templateIds = apiTemplates.map((t: any) => t.id);
          const elementsResult = await checkTemplateElementsStore.find({
            query: { templateId: { $in: templateIds } },
            paginate: false,
          });
          apiElements = elementsResult.data || elementsResult;
        }

        // 3. Merge elements into templates
        runInAction(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.templates = apiTemplates.map((t: any) => ({
            ...t,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            elements: apiElements.filter((el: any) => el.templateId === t.id),
          }));
          if (this.templates.length > 0 && !this.selectedTemplateId) {
            this.selectedTemplateId = this.templates[0].id;
            this.editingTemplate = JSON.parse(JSON.stringify(this.templates[0]));
          }
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.templates = [];
          this.loading = false;
        });
      }
    } catch (err) {
      runInAction(() => {
        this.error = "Failed to load templates";
        this.loading = false;
      });
    }
  }

  selectTemplate(templateId: string): void {
    this.selectedTemplateId = templateId;
    const template = this.templates.find((t) => t.id === templateId);
    if (template) {
      this.editingTemplate = JSON.parse(JSON.stringify(template));
      this.isDirty = false;
      this.selectedElementId = null;
    }
  }

  selectElement(elementId: string | null): void {
    this.selectedElementId = elementId;
  }

  updateElement(elementId: string, updates: Partial<CheckTemplateElement>): void {
    if (!this.editingTemplate) return;

    const element = this.editingTemplate.elements.find((e) => e.id === elementId);
    if (element) {
      Object.assign(element, updates);
      this.isDirty = true;
    }
  }

  updateTemplateField<K extends keyof CheckTemplate>(
    field: K,
    value: CheckTemplate[K]
  ): void {
    if (!this.editingTemplate) return;
    this.editingTemplate[field] = value;
    this.isDirty = true;
  }

  setPreviewScale(scale: number): void {
    this.previewScale = Math.max(0.3, Math.min(1.2, scale));
  }

  async saveTemplate(): Promise<void> {
    if (!this.editingTemplate) return;

    try {
      const checkTemplatesStore = this.rootStore.serviceStores?.["check-templates"];
      const checkTemplateElementsStore = this.rootStore.serviceStores?.["check-template-elements"];
      if (!checkTemplatesStore || !checkTemplateElementsStore) {
        throw new Error("Check templates stores not available");
      }

      // Separate template data from elements
      const { elements, ...templateData } = this.editingTemplate;
      // New templates have temp IDs starting with "template-"
      const isNewTemplate = this.editingTemplate.id.startsWith("template-");

      // 1. Save/create template
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let savedTemplate: any;
      if (isNewTemplate) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, createdAt, updatedAt, ...createData } = templateData;
        savedTemplate = await checkTemplatesStore.create(createData);
      } else {
        savedTemplate = await checkTemplatesStore.patch(templateData.id, templateData);
      }

      // 2. Save elements separately
      const savedElements: CheckTemplateElement[] = [];
      if (isNewTemplate) {
        // For new template: create all elements with new templateId
        for (const element of elements) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...elementData } = element;
          const savedElement = await checkTemplateElementsStore.create({
            ...elementData,
            templateId: savedTemplate.id,
          });
          savedElements.push(savedElement);
        }
      } else {
        // For existing: compare and create/patch/remove as needed
        const existingResult = await checkTemplateElementsStore.find({
          query: { templateId: savedTemplate.id },
          paginate: false,
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const existingElements: any[] = existingResult.data || existingResult;

        // Update/create elements
        for (const element of elements) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const existing = existingElements.find((e: any) => e.id === element.id);
          if (existing) {
            const savedElement = await checkTemplateElementsStore.patch(element.id, element);
            savedElements.push(savedElement);
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, ...elementData } = element;
            const savedElement = await checkTemplateElementsStore.create({
              ...elementData,
              templateId: savedTemplate.id,
            });
            savedElements.push(savedElement);
          }
        }

        // Remove deleted elements
        for (const existing of existingElements) {
          if (!elements.find((e) => e.id === existing.id)) {
            await checkTemplateElementsStore.remove(existing.id);
          }
        }
      }

      // Update local state with saved data (including API-generated IDs)
      const savedTemplateWithElements = {
        ...savedTemplate,
        elements: savedElements,
      };

      runInAction(() => {
        if (isNewTemplate) {
          // Replace the temp template with the saved one (with real ID)
          const tempIndex = this.templates.findIndex(
            (t) => t.id === this.editingTemplate!.id
          );
          if (tempIndex >= 0) {
            this.templates[tempIndex] = savedTemplateWithElements;
          } else {
            this.templates.push(savedTemplateWithElements);
          }
        } else {
          const existingIndex = this.templates.findIndex(
            (t) => t.id === savedTemplate.id
          );
          if (existingIndex >= 0) {
            this.templates[existingIndex] = savedTemplateWithElements;
          }
        }
        this.selectedTemplateId = savedTemplate.id;
        this.editingTemplate = JSON.parse(JSON.stringify(savedTemplateWithElements));
        this.isDirty = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = "Failed to save template";
      });
    }
  }

  createNewTemplate(category: TemplateCategory = "ap"): void {
    const newTemplate = this.createDefaultTemplate(category);
    // Add to templates array so it appears in the list immediately
    this.templates.push(newTemplate);
    // Set as selected (use the temp ID)
    this.selectedTemplateId = newTemplate.id;
    this.editingTemplate = JSON.parse(JSON.stringify(newTemplate));
    this.selectedElementId = null;
    this.isDirty = true;
  }

  async deleteTemplate(templateId: string): Promise<void> {
    try {
      const checkTemplatesStore = this.rootStore.serviceStores?.["check-templates"];
      if (checkTemplatesStore) {
        await checkTemplatesStore.remove(templateId);
      }

      runInAction(() => {
        this.templates = this.templates.filter((t) => t.id !== templateId);
        if (this.selectedTemplateId === templateId) {
          this.selectedTemplateId = this.templates[0]?.id || null;
          if (this.selectedTemplateId) {
            this.selectTemplate(this.selectedTemplateId);
          } else {
            this.editingTemplate = null;
          }
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = "Failed to delete template";
      });
    }
  }

  discardChanges(): void {
    if (this.selectedTemplateId) {
      this.selectTemplate(this.selectedTemplateId);
    } else {
      this.editingTemplate = null;
    }
    this.isDirty = false;
  }

  /**
   * Add a new element to the template
   */
  addElement(section: CheckSectionType, elementType: CheckElementType): void {
    if (!this.editingTemplate) return;

    // Find element definition based on template category
    const elementDefs = getAvailableElementTypes(
      section,
      this.editingTemplate.layoutType,
      this.editingTemplate.templateCategory || "ap"
    );
    const def = elementDefs.find((d) => d.type === elementType);
    if (!def) return;

    // Generate unique ID
    const newId = `elem-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Calculate sortOrder
    const existingElements = this.editingTemplate.elements.filter(
      (e) => e.section === section
    );
    const maxSortOrder = existingElements.reduce(
      (max, e) => Math.max(max, e.sortOrder || 0),
      -1
    );

    const newElement: CheckTemplateElement = {
      id: newId,
      elementType,
      section,
      label: def.label,
      x: 0.25,
      y: 0.25 + existingElements.length * 0.3, // Stack new elements vertically
      width: def.defaultWidth,
      fontSize: def.defaultFontSize,
      fontWeight: def.defaultFontWeight,
      alignment: def.defaultAlignment,
      visible: true,
      sortOrder: maxSortOrder + 1,
    };

    this.editingTemplate.elements.push(newElement);
    this.selectedElementId = newId;
    this.isDirty = true;
  }

  /**
   * Remove an element from the template
   */
  removeElement(elementId: string): void {
    if (!this.editingTemplate) return;

    const index = this.editingTemplate.elements.findIndex((e) => e.id === elementId);
    if (index >= 0) {
      this.editingTemplate.elements.splice(index, 1);
      if (this.selectedElementId === elementId) {
        this.selectedElementId = null;
      }
      this.isDirty = true;
    }
  }

  // ==================== Default Template ====================

  private createDefaultTemplate(category: TemplateCategory = "ap"): CheckTemplate {
    const plantCode =
      this.rootStore.appLayoutStore?.selectedCompanyId || "ALL";

    const isPayroll = category === "payroll";

    return {
      id: `template-${Date.now()}`,
      name: isPayroll ? "New Payroll Check Template" : "New AP Check Template",
      companyCode: plantCode,
      layoutType: "check-top", // Check at top, stubs below (common for payroll)
      templateCategory: category,

      // Standard check stock heights
      stubTopHeight: isPayroll ? 3.5 : 2.75, // Larger stubs for payroll
      checkHeight: 3.5,
      stubBottomHeight: isPayroll ? 4.0 : 4.75,
      pageHeight: 11,

      offsetX: 0,
      offsetY: 0,
      marginTop: 0,
      marginLeft: 0.25,
      marginRight: 0.25,

      elements: [],

      // Company/bank info (pre-printed on stock)
      companyName: "",
      companyAddress: "",
      companyPhone: "",
      bankName: "",
      bankCity: "",
      bankRouting: "",

      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

}


export default CheckTemplateDesignerStore;
